import { useMemo, useRef, useState } from 'react';
import './NavigationRibbon.scss';
import { useDashboardDomainContext } from "../../DashboardDomainProvider";
import { IconControl, ContextMenuControl } from 'spark-core-dx/components';
import DashboardPageDialog from '../dialogs/DashboardPageDialog.jsx';
import TabWrapper from './TabWrapper.jsx';
import DeleteDialog from '../dialogs/DeleteDialog.jsx';
import DeletePageDialog from '../dialogs/DeletePageDialog.jsx';
import RevertChangesDialog from '../dialogs/RevertChangesDialog.jsx';
import PublishDialog from '../dialogs/PublishDialog.jsx';
import MigrateDashboardModelWorkspaceDialog from '../dialogs/MigrateDashboardModelWorkspaceDialog.jsx';
import InactiveVisualsDialog from '../dialogs/InactiveVisualsDialog.jsx';
import { useCorePermissions } from 'spark-core-dx/hooks';

const NavigationRibbon = () => {
  const {
    pages,
    selectedPageData,
    isViewMode,
    isModelDesigner,
    isVisualDesigner,
    hasModels,
    inactivePageVisuals,
    switchMode,
    navigateToDashboardList,
    navigateToModelDesigner,
    navigateToVisualDesignerFromIcon,
    createPage,
    clonePage,
    editPage,
    deletePage,
    restorePage,
    canEditDashboard,
    canDeleteDashboard,
    canHardDeleteDashboard,
  } = useDashboardDomainContext();

  const { canDelete, canHardDelete } = useCorePermissions();
  const [dashboardContextMenu, setDashboardContextMenu] = useState(false);
  const [dashboardContextStyle, setDashboardContextStyle] = useState({});
  const [pageContextMenu, setPageContextMenu] = useState(false);
  const [pageContextMenuStyle, setPageContextMenuStyle] = useState();
  const [dialogType, setDialogType] = useState("");
  const scrollRef = useRef(null);

  const activePage = pages.find((page) => page.Key === pageContextMenu) ?? selectedPageData;
  const canDeletePages = canDelete("Dashboard");
  const canHardDeletePages = canHardDelete("Dashboard");
  const canOpenVisualDesigner = !isModelDesigner || hasModels;

  const renderContextMenuOptions = (optionList) => (
    <div className="options-wrapper">
      {optionList.map((opt) => (
        <div
          key={opt.text}
          className={`option${opt.disabled ? ' disabled' : ''}`}
          onClick={!opt.disabled ? opt.onClick : undefined}
        >
          <IconControl iconKey={opt.icon} />
          {opt.text}
        </div>
      ))}
    </div>
  );

  const openDashboardContext = (e) => {
    const iconBox = e.currentTarget?.getBoundingClientRect?.();
    setDashboardContextMenu(!dashboardContextMenu);
    setDashboardContextStyle({
      left: iconBox?.left ?? 0,
      bottom: 42,
      position: 'fixed',
    });
  };

  const openPageContext = (e, pageKey) => {
    const tabBox = e.currentTarget?.closest?.('.tab')?.getBoundingClientRect?.();
    const menuWidth = 12 * 16;
    setPageContextMenu(pageKey);
    setPageContextMenuStyle({
      position: 'fixed',
      left: (((tabBox?.left ?? 0) + ((tabBox?.width ?? 0) / 2)) - (menuWidth / 2)) + 12,
      bottom: window.innerHeight - (tabBox?.top ?? 0),
    });
  };

  const dashboardContextMenuOptions = useMemo(() => {
    const options = [
      {
        text: "Back To List",
        icon: "arrowleft",
        onClick: () => navigateToDashboardList(),
      },
    ];

    if (isViewMode && canEditDashboard && isVisualDesigner) {
      options.push({
        text: "Design",
        icon: "edit",
        onClick: () => switchMode("design"),
      });
    }

    if (!isViewMode) {
      if (isVisualDesigner) {
        options.push({
          text: "Exit Design Mode",
          icon: "close",
          onClick: () => switchMode("view"),
        });
        options.push({
          text: "Add Page",
          icon: "add",
          onClick: () => setDialogType("page-create"),
        });
      }

      if (isVisualDesigner) {
        options.push({
          text: "Publish",
          icon: "upload",
          onClick: () => setDialogType("publish"),
        });
        options.push({
          text: "Migrate Model Workspace",
          icon: "refresh",
          onClick: () => setDialogType("migrate-workspace"),
        });
      }
      options.push({
        text: "Revert Changes",
        icon: "undo",
        onClick: () => setDialogType("revertchanges"),
      });
    }

    if (canDeleteDashboard) {
      options.push({
        text: "Hide",
        icon: "remove",
        onClick: () => setDialogType("dashboard-delete"),
      });
    }

    if (canHardDeleteDashboard) {
      options.push({
        text: "Delete",
        icon: "delete",
        onClick: () => setDialogType("dashboard-hard-delete"),
      });
    }

    return options;
  }, [
    canDeleteDashboard,
    canEditDashboard,
    canHardDeleteDashboard,
    isViewMode,
    isVisualDesigner,
    navigateToDashboardList,
    switchMode,
  ]);

  const pageContextMenuOptions = useMemo(() => {
    if (!activePage) {
      return [];
    }

    const options = [];

    if (!activePage?.IsDeleted) {
      options.push({
        text: "Edit Page",
        icon: "edit",
        disabled: isViewMode,
        onClick: () => setDialogType("page-edit"),
      });
      options.push({
        text: "Copy Page",
        icon: "clone",
        disabled: isViewMode,
        onClick: () => {
          setPageContextMenu(false);
          clonePage(activePage.Key);
        },
      });
      options.push({
        text: "Page Visuals",
        icon: "viewlist",
        disabled: isViewMode || (inactivePageVisuals?.length ?? 0) === 0,
        onClick: () => setDialogType("inactive-visuals"),
      });
    }

    if (!activePage?.IsDeleted && canDeletePages) {
      options.push({
        text: "Hide Page",
        icon: "remove",
        disabled: isViewMode,
        onClick: () => setDialogType("page-delete"),
      });
    }

    if (activePage?.IsDeleted) {
      options.push({
        text: "Restore Page",
        icon: "undo",
        disabled: isViewMode,
        onClick: () => restorePage(activePage.Key),
      });
    }

    if (canHardDeletePages) {
      options.push({
        text: "Delete Page",
        icon: "delete",
        disabled: isViewMode,
        onClick: () => setDialogType("page-hard-delete"),
      });
    }

    return options;
  }, [activePage, canDeletePages, canHardDeletePages, clonePage, inactivePageVisuals?.length, isViewMode, restorePage]);

  return (
    <div className="dashboard-navigation">
      <DashboardPageDialog
        isActive={dialogType === "page-create"}
        closeDialog={() => setDialogType("")}
        onSave={async (page) => (await createPage(page))?.Success}
      />
      <DashboardPageDialog
        isActive={dialogType === "page-edit"}
        closeDialog={() => setDialogType("")}
        page={activePage}
        onSave={async (page) => (await editPage(activePage?.Key, page))?.Success}
      />
      <DeleteDialog
        isActive={dialogType === "dashboard-delete"}
        closeDialog={() => setDialogType("")}
      />
      <DeleteDialog
        isActive={dialogType === "dashboard-hard-delete"}
        closeDialog={() => setDialogType("")}
        deletePermanently
      />
      <DeletePageDialog
        isActive={dialogType === "page-delete"}
        closeDialog={() => setDialogType("")}
        pageName={activePage?.Name}
        onConfirm={() => deletePage(activePage?.Key, false)}
      />
      <DeletePageDialog
        isActive={dialogType === "page-hard-delete"}
        closeDialog={() => setDialogType("")}
        deleteMode="hard"
        pageName={activePage?.Name}
        onConfirm={() => deletePage(activePage?.Key, true)}
      />
      <RevertChangesDialog isActive={dialogType === "revertchanges"} closeDialog={() => setDialogType("")} />
      <PublishDialog isActive={dialogType === "publish"} closeDialog={() => setDialogType("")} />
      <MigrateDashboardModelWorkspaceDialog isActive={dialogType === "migrate-workspace"} closeDialog={() => setDialogType("")} />
      <InactiveVisualsDialog isActive={dialogType === "inactive-visuals"} closeDialog={() => setDialogType("")} />

      {pageContextMenu && (
        <ContextMenuControl
          openContextMenu={pageContextMenu}
          setOpenContextMenu={setPageContextMenu}
          contextMenuStyle={pageContextMenuStyle}
        >
          {renderContextMenuOptions(pageContextMenuOptions)}
        </ContextMenuControl>
      )}

      {dashboardContextMenu && (
        <ContextMenuControl
          openContextMenu={dashboardContextMenu}
          setOpenContextMenu={setDashboardContextMenu}
          contextMenuStyle={dashboardContextStyle}
        >
          {renderContextMenuOptions(dashboardContextMenuOptions)}
        </ContextMenuControl>
      )}

      <div className="menu-buttons left">
        <IconControl
          className={`menu-button-icon mode-switch-icon${!canOpenVisualDesigner ? " disabled" : ""}`}
          iconKey={isModelDesigner ? "dashboard" : "schema"}
          title={
            isModelDesigner
              ? (canOpenVisualDesigner ? "Visual Designer" : "Visual Designer unavailable until this dashboard has models")
              : "Model Designer"
          }
          onClick={() => {
            if (!canOpenVisualDesigner) {
              return;
            }

            if (isModelDesigner) {
              navigateToVisualDesignerFromIcon();
              return;
            }

            navigateToModelDesigner();
          }}
        />
        <IconControl
          className={`menu-button-icon filled`}
          iconKey={"dashboard"}
          onClick={openDashboardContext}
        />
      </div>

      {!isModelDesigner && (
        <TabWrapper
          scrollRef={scrollRef}
          handleContextMenu={openPageContext}
          showAddPageButton={!isViewMode}
          onAddPage={() => setDialogType("page-create")}
        />
      )}

      {isModelDesigner && <div className="model-mode-spacer" />}
    </div>
  );
};

export default NavigationRibbon;
